import React, { useContext, useEffect, useState } from 'react'
import API from '../../../services/API'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { DataGrid } from '@mui/x-data-grid'
import moment from 'moment'
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Drawer,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material'
import DetailsBox, { DetailRow } from '../../../components/Cards/DetailsBox'
import SendIcon from '@mui/icons-material/Send'
import LogsComponent from '../../../components/Logs/LogsComponent'
import { GlobalContext, UserContext } from '../../../App'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'

const SingleQuery = ({ queryID }) => {
  const [query, setQuery] = useState({})
  const [newLog, setNewLog] = useState('')
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)

  useEffect(() => {
    API.get('/website-enquiry?queryID=' + queryID).then((res) =>
      setQuery(res.data)
    )
  }, [])

  const handleNewLog = (e) => {
    e.preventDefault()
    API.post('/website-enquiry-update?queryID=' + queryID, {
      newLog,
    }).then((res) => setQuery(res.data))
  }

  const handleStatusChange = () => {
    API.post('/website-enquiry-update?queryID=' + queryID, {
      status: 'Completed',
    }).then((res) => setQuery(res.data))
  }

  const handleDelete = () => {
    API.post('/website-enquiry-update?queryID=' + queryID, {
      archived: true,
    }).then((res) => setQuery(res.data))
  }

  const handlePermanentlyDelete = () => {
    API.delete('/website-enquiry?queryID=' + queryID).then((res) => {
      if (res.status === 200) {
        setQuery({})
        setToast({ message: 'Permanently Deleted' })
      }
    })
  }

  const handleUndoArchive = () => {
    API.post('/website-enquiry-update?queryID=' + queryID, {
      archived: false,
    }).then((res) => res.status === 200 && setQuery(res.data))
  }

  const handleAssignToMe = () => {
    API.post('/website-enquiry-update?queryID=' + queryID, {
      assignedTo: user.employee._id,
      status: 'In Progress',
    }).then((res) => setQuery(res.data))
  }
  return (
    <Stack width="40rem" maxWidth="100vw" padding={1} gap={2}>
      <DetailsBox
        title={moment(query?.createdAt).format('Do MMM YYYY HH:mm')}
        rows={[
          { label: 'Name', value: query.name },
          { label: 'Email', value: query.email },
          { label: 'Phone', value: query.phoneNumber },
          { label: 'Subject', value: query.subject },
          { label: 'Message', value: query.message },
          { label: 'Status', value: query.status },
          {
            label: 'Assigned To',
            value: query.assignedTo && (
              <Stack gap={1} direction="row" alignItems="center">
                <Avatar
                  src={query.assignedTo.userAccount.photograph}
                  sx={{ width: 18, height: 18, borderRadius: 1 }}
                />
                {query.assignedTo.userAccount.firstName}{' '}
                {query.assignedTo.userAccount.lastName}
              </Stack>
            ),
          },
        ]}
      />
      {query.archived ? (
        <Alert severity="error">
          <AlertTitle>This Query has been Archived</AlertTitle>
          <ButtonGroup>
            <Button
              onClick={handlePermanentlyDelete}
              variant="contained"
              color="error"
              size="small"
            >
              Permanently Delete
            </Button>
            <Button
              onClick={handleUndoArchive}
              variant="contained"
              color="info"
              size="small"
            >
              Restore
            </Button>
          </ButtonGroup>
        </Alert>
      ) : (
        <ButtonGroup>
          {!query.assignedTo && (
            <Button
              onClick={handleAssignToMe}
              color="primary"
              variant="outlined"
            >
              Assign to me
            </Button>
          )}
          <Button
            onClick={handleStatusChange}
            color="success"
            variant="contained"
          >
            Mark as Complete
          </Button>
          <Button onClick={handleDelete} color="error" variant="outlined">
            Delete
          </Button>
        </ButtonGroup>
      )}
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        gap={{ xs: 0, lg: 1 }}
        component="form"
        onSubmit={handleNewLog}
      >
        <TextField
          placeholder="Add a New Note"
          value={newLog}
          onChange={(e) => setNewLog(e.target.value)}
          fullWidth
          size="small"
        />
        <IconButton type="submit">
          <SendIcon color="success" />
        </IconButton>
      </Stack>
      <LogsComponent logs={query.logs ?? []} />
    </Stack>
  )
}

const WebsiteQueries = () => {
  const [queries, setQueries] = useState([])
  const [fetchError, setFetchError] = useState(false)
  const [includeArchived, setIncludeArchived] = useState(false)
  const [selectedRow, setSelectedRow] = useState(false)
  const [showCompleted, setShowCompleted] = useState(false)

  const getQueries = () => {
    API.get(
      `/website-enquiries?includearchived=${includeArchived}&showcompleted=${showCompleted}`
    ).then((res) => {
      if (res.status === 200) {
        setQueries(res.data)
      } else setFetchError(true)
    })
  }

  useEffect(() => {
    getQueries()
  }, [includeArchived, showCompleted])

  return (
    <Stack component={Paper} padding={2} minHeight={200} gap={2}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
      >
        <Typography variant="title2">Website Enquiries</Typography>
        <ButtonGroup size="small">
          <Button
            variant={showCompleted ? 'contained' : 'outlined'}
            endIcon={showCompleted ? <VisibilityIcon /> : <VisibilityOffIcon />}
            onClick={() => setShowCompleted(!showCompleted)}
          >
            Completed
          </Button>
          <Button
            variant={includeArchived ? 'contained' : 'outlined'}
            endIcon={
              includeArchived ? <VisibilityIcon /> : <VisibilityOffIcon />
            }
            onClick={() => setIncludeArchived(!includeArchived)}
          >
            Archived
          </Button>
        </ButtonGroup>
      </Stack>
      <DataGrid
        getRowId={(params) => params._id}
        rows={queries}
        columns={[
          {
            field: 'date',
            headerName: 'Date',
            renderCell: ({ row }) =>
              moment(row.createdAt).format('Do MMM YYYY HH:mm'),
            valueGetter: (value, row) => moment(row.createdAt).unix(),
            flex: 0.5,
            minWidth: 170
          },
          {
            field: 'name',
            headerName: 'Name',
            valueGetter: (value, row) => row.name,
            flex: 1,
            minWidth: 150
          },
          { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
          {
            field: 'assignedTo',
            headerName: 'Assigned To',
            headerAlign: 'center',
            align: 'center',
            minWidth: 200,
            valueGetter: (value, row) => row?.assignedTo?._id,
            renderCell: ({ row }) =>
              row.assignedTo && (
                <Tooltip
                  title={
                    row.assignedTo.userAccount.firstName +
                    ' ' +
                    row.assignedTo.userAccount.lastName
                  }
                >
                  <Avatar
                    src={row?.assignedTo?.userAccount?.photograph}
                    sx={{ width: 24, height: 24, borderRadius: 1 }}
                  />
                </Tooltip>
              ),
          },
          {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            align: 'right',
            minWidth: 150,
            headerAlign: 'right',
            valueGetter: (value, row) => row.status,
            renderCell: ({ row }) => (
              <Chip
                color={
                  row.status === 'New' || row.archived
                    ? 'error'
                    : row.status === 'In Progress'
                    ? 'warning'
                    : 'success'
                }
                label={row.archived ? 'Archived' : row.status}
                size="small"
                sx={{ borderRadius: 1 }}
              />
            ),
          },
        ]}
        disableRowSelectionOnClick
        sx={{
          '.MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
        }}
        onRowClick={({ row }) => setSelectedRow(row._id)}
      />
      <Drawer
        anchor="right"
        open={!!selectedRow}
        onClose={() => {
          setSelectedRow(null)
          getQueries()
        }}
      >
        <SingleQuery queryID={selectedRow} />
      </Drawer>
    </Stack>
  )
}

export default WebsiteQueries
